import * as Sentry from '@sentry/browser';
import { message } from 'antd';
import { observable, action, makeObservable, runInAction, computed } from 'mobx';
import { DeviceAllowedActions } from '@/constants/enums';
import { getPoweredOnState } from '@/comp/StationControlCenter/SccUtils';
import epaxios from '@/utils/epaxios';
import i18n from '@/i18n/i18n';
import RootStore from '../RootStore';
import { Device } from '../Station/constants';
import { t } from 'i18next';
import { DeviceType } from '@/constants/deviceTypeEnum';
import type { DeviceDrawerSchema, DeviceSCCSchema } from '@/comp/StationControlCenter/types/device-schema.types';

export default class ControlCenterStore {
  rootStore: typeof RootStore;
  @observable isPendingPoweredOnStateChange = new Map();
  @observable isChangingMaintenanceState = new Map();
  @observable erroredDeviceId = null;
  @observable deviceSCCSchemas: DeviceSCCSchema[] = [];
  @observable deviceDrawerSchemas: DeviceDrawerSchema[] = [];
  @observable deviceSccSchemaByDeviceId = new Map<string, DeviceSCCSchema>();
  @observable deviceDrawerSchemaByDeviceId = new Map<string, DeviceDrawerSchema>();
  @observable isSchemasLoading = false;
  @observable schemasError: string | null = null;
  @observable private schemasStationId: string | null = null;
  private universalSchemasInFlight = new Map<string, Promise<void>>();
  POWER_TOGGLE_TIMEOUT_SEC = 30 * 1000;

  constructor(rootStore: typeof RootStore) {
    makeObservable(this);
    this.rootStore = rootStore;
  }

  @computed get deviceSccSchemaByDeviceType(): Map<DeviceType, DeviceSCCSchema> {
    const map = new Map<DeviceType, DeviceSCCSchema>();
    for (const schema of this.deviceSCCSchemas) {
      map.set(schema.deviceType as DeviceType, schema);
    }
    return map;
  }

  @computed get deviceDrawerSchemaByDeviceType(): Map<DeviceType, DeviceDrawerSchema> {
    const map = new Map<DeviceType, DeviceDrawerSchema>();
    for (const schema of this.deviceDrawerSchemas) {
      map.set(schema.deviceType as DeviceType, schema);
    }
    return map;
  }

  getDeviceSccSchema = (deviceType: DeviceType) => {
    return this.deviceSccSchemaByDeviceType.get(deviceType);
  };

  getDeviceDrawerSchema = (deviceType: DeviceType) => {
    return this.deviceDrawerSchemaByDeviceType.get(deviceType);
  };

  /**
   * For General devices schemas can be device-specific.
   * These helpers resolve schema using deviceId first (if available),
   * with fallback to general deviceType schema.
   */
  getDeviceSccSchemaForDevice = (deviceId: string) => {
    const device = this.rootStore.stationStore.getDeviceById(deviceId);
    if (!device) {
      return null;
    }

    const isGeneralDevice = device.deviceType === DeviceType.General;
    const hasDeviceSpecificSchema = deviceId && this.deviceSccSchemaByDeviceId.has(deviceId);
    if (isGeneralDevice && hasDeviceSpecificSchema) {
      return this.deviceSccSchemaByDeviceId.get(deviceId);
    }

    return this.getDeviceSccSchema(device.deviceType);
  };

  getDeviceDrawerSchemaForDevice = (device: Device) => {
    const isGeneralDevice = device.deviceType === DeviceType.General;
    const hasDeviceSpecificSchema = device.deviceId && this.deviceDrawerSchemaByDeviceId.has(device.deviceId);
    if (isGeneralDevice && hasDeviceSpecificSchema) {
      return this.deviceDrawerSchemaByDeviceId.get(device.deviceId);
    }

    return this.getDeviceDrawerSchema(device.deviceType);
  };

  private resetSchemasCacheForStation = (stationId: string | null) => {
    this.schemasStationId = stationId;
    this.deviceSCCSchemas = [];
    this.deviceDrawerSchemas = [];
    this.deviceSccSchemaByDeviceId.clear();
    this.deviceDrawerSchemaByDeviceId.clear();
    this.universalSchemasInFlight.clear();
  };

  private fetchGeneralDeviceSchemasByDeviceId = async (stationId: string, deviceId: string): Promise<void> => {
    const key = deviceId.trim();
    if (!key) {
      return;
    }

    const hasBothSchemas = this.deviceSccSchemaByDeviceId.has(key) && this.deviceDrawerSchemaByDeviceId.has(key);
    if (hasBothSchemas) {
      return;
    }

    const existing = this.universalSchemasInFlight.get(key);
    if (existing) {
      return existing;
    }

    const promise = (async (): Promise<void> => {
      const errors: string[] = [];

      try {
        const [sccRes, drawerRes] = await Promise.allSettled([
          epaxios.get<DeviceSCCSchema>(
            `station-mgmt/station/${stationId}/generic-asset-schema/scc/general/${deviceId}`,
          ),
          epaxios.get<DeviceDrawerSchema>(
            `station-mgmt/station/${stationId}/generic-asset-schema/asset-info/general/${deviceId}`,
          ),
        ]);

        runInAction(() => {
          if (sccRes.status === 'fulfilled') {
            const schema = sccRes.value.data;
            if (schema) {
              this.deviceSccSchemaByDeviceId.set(key, schema);
            }
          } else {
            errors.push(`Failed to load SCC schema for deviceId=${key}`);
          }

          if (drawerRes.status === 'fulfilled') {
            const schema = drawerRes.value.data;
            if (schema) {
              this.deviceDrawerSchemaByDeviceId.set(key, schema);
            }
          } else {
            errors.push(`Failed to load drawer schema for deviceId=${key}`);
          }

          if (errors.length > 0) {
            this.schemasError = this.schemasError ? `${this.schemasError}; ${errors.join('; ')}` : errors.join('; ');
          }
        });
      } catch (error) {
        runInAction(() => {
          errors.push(`Failed to load schemas for deviceId=${key}`);
          this.schemasError = this.schemasError ? `${this.schemasError}; ${errors.join('; ')}` : errors.join('; ');
        });
      }
    })();

    this.universalSchemasInFlight.set(key, promise);
    return promise.finally(() => {
      this.universalSchemasInFlight.delete(key);
    });
  };

  @action fetchDeviceSchemas = async (opts?: { stationId?: string; generalDevicesIds?: string[] }): Promise<void> => {
    const stationId = opts?.stationId ?? this.rootStore.stationStore.currentStation?.id ?? null;
    const nextDevicesIds = (opts?.generalDevicesIds ?? [])
      .map(id => id?.trim())
      .filter((id): id is string => Boolean(id));
    const uniqueDevicesIds = Array.from(new Set(nextDevicesIds)).sort();

    const stationChanged = stationId !== this.schemasStationId;
    if (stationChanged) {
      runInAction(() => {
        this.resetSchemasCacheForStation(stationId);
      });
    }

    const hasNoBaseSchemas = this.deviceSCCSchemas.length === 0 || this.deviceDrawerSchemas.length === 0;
    const shouldFetchBase = stationChanged || hasNoBaseSchemas;

    this.isSchemasLoading = true;
    this.schemasError = null;

    try {
      const errors: string[] = [];

      if (shouldFetchBase && stationId) {
        try {
          const [sccRes, drawerRes] = await Promise.allSettled([
            epaxios.get<DeviceSCCSchema[]>(`/station-mgmt/station/${stationId}/generic-asset-schema/scc`),
            epaxios.get<DeviceDrawerSchema[]>(`/station-mgmt/station/${stationId}/generic-asset-schema/asset-info`),
          ]);

          runInAction(() => {
            if (sccRes.status === 'fulfilled') {
              this.deviceSCCSchemas = sccRes.value.data || [];
            } else {
              errors.push('Failed to load SCC schemas');
            }

            if (drawerRes.status === 'fulfilled') {
              this.deviceDrawerSchemas = drawerRes.value.data || [];
            } else {
              errors.push('Failed to load drawer schemas');
            }
          });
        } catch (error) {
          errors.push('Failed to load base schemas');
        }
      }

      const missingDevicesIds = uniqueDevicesIds.filter(
        id => !this.deviceSccSchemaByDeviceId.has(id) || !this.deviceDrawerSchemaByDeviceId.has(id),
      );
      if (missingDevicesIds.length > 0 && stationId) {
        try {
          await Promise.all(
            missingDevicesIds.map(deviceId => this.fetchGeneralDeviceSchemasByDeviceId(stationId, deviceId)),
          );
        } catch (error) {
          errors.push('Failed to load device schemas');
        }
      }

      if (errors.length > 0) {
        runInAction(() => {
          this.schemasError = errors.join('; ');
        });
        throw new Error(errors.join('; '));
      }
    } catch (error) {
      runInAction(() => {
        this.schemasError = error instanceof Error ? error.message : 'Failed to load device schemas';
      });
      throw error;
    } finally {
      runInAction(() => {
        this.isSchemasLoading = false;
      });
    }
  };

  @action setIsGoingToDeactivateDueToMaintenance = async (deviceId: string, inMaintenance: boolean) => {
    if (inMaintenance) {
      return false;
    }
    try {
      const isGoingToDeactivateDueToMaintenance = await epaxios.get(
        `/station/${this.rootStore.stationStore.currentStation.id}/proactive-engine/device/${deviceId}/is-engine-deactivation-confirmation-needed`,
      );
      return isGoingToDeactivateDueToMaintenance?.data || false;
    } catch (e) {
      return false;
    }
  };

  fetchMaintenanceMessage = async (deviceId: string, inMaintenance: boolean) => {
    if (inMaintenance) {
      return t('Are you sure you want to mark this device as not in maintenance?');
    }

    const isGoingToDeactivate = await this.setIsGoingToDeactivateDueToMaintenance(deviceId, inMaintenance);
    if (isGoingToDeactivate) {
      return t('goingToMaintenanceMessage');
    }
    return t('Are you sure you want to mark this device as In maintenance?');
  };

  @action setMaintenance = async (deviceId, isInMaintenance) => {
    this.isChangingMaintenanceState.set(deviceId, true);

    try {
      const { data }: { data: Device } = await epaxios.put(
        `/station/${this.rootStore.stationStore.currentStation.id}/device/${deviceId}/inMaintenance`,
        { inMaintenance: isInMaintenance },
      );
      const oldDevice = this.rootStore.stationStore.getDeviceById(deviceId);
      if (oldDevice) {
        oldDevice.inMaintenance = data.inMaintenance;
      }
    } catch (e) {
      message.error(i18n.t('Failed changing maintenance mode'));
      return Promise.reject();
    } finally {
      this.isChangingMaintenanceState.set(deviceId, false);
      this.rootStore.stationRiskStore.reset();
    }
  };

  @action restoreStateOnTimerExpOrFailure = async deviceId => {
    const errorMessage = i18n.t('Failed changing compressor power state');
    Sentry.captureException(errorMessage);
    this.isPendingPoweredOnStateChange.delete(deviceId);
    this.erroredDeviceId = deviceId;
  };

  @action resetError = async () => {
    this.erroredDeviceId = null;
  };

  @action updateDeviceStateOnMetricUpdate = async deviceId => {
    const device = this.rootStore.stationStore.getDeviceById(deviceId);
    if (device && this.isPendingPoweredOnStateChange.get(deviceId)) {
      clearTimeout(this.isPendingPoweredOnStateChange.get(deviceId));
      this.isPendingPoweredOnStateChange.delete(deviceId);
    }
  };

  @action toggleOnOff = async deviceId => {
    const action = getPoweredOnState(this.rootStore.stationStore.getDeviceById(deviceId).poweredOn)
      ? DeviceAllowedActions.PowerOff
      : DeviceAllowedActions.PowerOn;
    const payload = {
      deviceId: deviceId,
      action: action,
      reason: `Power state changed from Station Control Center`,
      force: true,
    };
    try {
      const timeoutId = setTimeout(this.restoreStateOnTimerExpOrFailure, this.POWER_TOGGLE_TIMEOUT_SEC, deviceId);
      this.isPendingPoweredOnStateChange.set(deviceId, timeoutId);
      await epaxios.post(
        `/ecoboxAPI/insert-data/station/${this.rootStore.stationStore.currentStation.id}/by-action`,
        payload,
      );
    } catch (e) {
      await this.restoreStateOnTimerExpOrFailure(deviceId);
      return Promise.reject();
    }
  };
}
